import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAppointmentsByDate, fetchAppointmentsByDateRangeOnly, fetchAppointmentById } from "../../../store/thunks/appointmentsThunk";
import { fetchTodos } from "../../../store/thunks/todoThunk";
import { setSelectedAppointment } from "../../../store/slices/appointmentsSlice";
import { logError } from "../../../utils/logger";
import FrontDeskTodoItem from "../../../applicationUi/components/FrontDeskTodoItem";
import FrontDeskAppointmentItem from "../../../applicationUi/components/FrontDeskAppointmentItem";
import FrontDeskQuickActions from "../../../applicationUi/components/FrontDeskQuickActions";
import AppointmentsSection from "../../../applicationUi/components/appointments/AppointmentsSection";
import AppointmentSummary from "../../../applicationUi/components/appointments/AppointmentSummary";
import { Tab } from "@headlessui/react";
import { CalendarIcon, CheckCircleIcon, ExclamationCircleIcon, ClockIcon, DocumentTextIcon, PhoneIcon, ArrowUpIcon, ArrowDownIcon } from "@heroicons/react/24/outline";
import { differenceInMinutes, format, addDays } from "date-fns";

export default function FrontDeskDashboard({ onAppointmentClick, onReschedule, onWideSidePanelClose }) {
  const dispatch = useDispatch();
  const appointments = useSelector((state) => state.appointments.scheduleAppointments || []);
  const sevenDayAppointments = useSelector((state) => state.appointments.sevenDayAppointments || []);
  const todosState = useSelector((state) => state.todos);
  const todos = todosState.todos || {};
  const user = useSelector((state) => state.auth.user || {});
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [dateFilter, setDateFilter] = useState("today");
  const [viewType, setViewType] = useState("timeline");
  const [todaysAppointments, setTodaysAppointments] = useState([]);
  const [wideSidePanelOpen, setWideSidePanelOpen] = useState(false);
  const [wideSidePanelAppointment, setWideSidePanelAppointment] = useState(null);
  const [sortOrder, setSortOrder] = useState("newest");

  useEffect(() => {
    // Get today's date at midnight
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Get tomorrow's date at midnight
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // Get next week's date
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);

    // Get next month's date
    const nextMonth = new Date(today);
    nextMonth.setMonth(nextMonth.getMonth() + 1);

    // Fetch appointments based on the selected date filter
    if (dateFilter === "week" || dateFilter === "month") {
      // For week/month view, use fetchAppointmentsByDateRangeOnly
      dispatch(fetchAppointmentsByDateRangeOnly({
        startDate: today,
        endDate: dateFilter === "week" ? nextWeek : nextMonth
      }))
        .unwrap()
        .catch((error) => {
          logError(`Failed to fetch ${dateFilter} appointments:`, {
            componentName: "FrontDeskDashboard",
            action: "fetchAppointmentsByDateRangeOnly",
            error: error.message,
          });
        });
    } else {
      // For today/tomorrow, use fetchAppointmentsByDate
      let dateToFetch = dateFilter === "tomorrow" ? tomorrow : today;
      
      dispatch(fetchAppointmentsByDate({ date: dateToFetch }))
        .unwrap()
        .catch((error) => {
          logError("Failed to fetch appointments:", {
            componentName: "FrontDeskDashboard",
            action: "fetchAppointmentsByDate",
            error: error.message,
          });
        });
    }
    
    // Get the user's role - use admin role for front desk
    const userRole = "admin";
    dispatch(fetchTodos({ userRole }));
  }, [dispatch, dateFilter]);

  // Fetch today's appointments separately
  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    dispatch(fetchAppointmentsByDate({ date: today }))
      .unwrap()
      .then((response) => {
        setTodaysAppointments(response || []);
      })
      .catch((error) => {
        logError("Failed to fetch today's appointments:", {
          componentName: "FrontDeskDashboard",
          action: "fetchAppointmentsByDate",
          error: error.message,
        });
      });
  }, [dispatch]);

  const getFilteredAppointments = () => {
    if (dateFilter === "week" || dateFilter === "month") {
      return sevenDayAppointments;
    }
    return appointments;
  };

  const handleAppointmentClick = (appointment) => {
    onAppointmentClick(appointment);
  };

  const handleCloseSummary = () => {
    setIsSummaryOpen(false);
    setSelectedAppointment(null);
  };

  // Get today's appointments for the card display
  const getTodaysAppointments = () => {
    return todaysAppointments;
  };

  const getTodoCount = (type) => {
    // Map the type to the correct category key
    const categoryMap = {
      "confirm": "Confirm",
      "invoice": "Invoice",
      "payment": "Payment"
    };
    
    const category = categoryMap[type];
    if (!category || !todos[category]) return 0;
    return todos[category].length;
  };

  const getTodoItems = (type) => {
    // Map the type to the correct category key
    const categoryMap = {
      "confirm": "Confirm",
      "invoice": "Invoice",
      "payment": "Payment"
    };
    
    const category = categoryMap[type];
    if (!category || !todos[category]) return [];
    
    // Transform the todo items to include the type
    let items = todos[category].map(todo => ({
      ...todo,
      type: category
    }));

    // Sort the items based on sortOrder
    items.sort((a, b) => {
      const dateA = new Date(a.start);
      const dateB = new Date(b.start);
      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });

    return items;
  };

  const groupAppointmentsByTime = (appointments) => {
    const grouped = {};
    
    // Filter out appointments with invalid dates
    const validAppointments = appointments.filter(appointment => {
      try {
        const startDate = new Date(appointment.start);
        const endDate = new Date(appointment.end);
        return !isNaN(startDate) && !isNaN(endDate);
      } catch (error) {
        console.error("Invalid date in appointment:", appointment);
        return false;
      }
    });
    
    // Group by day for week view
    if (dateFilter === "week") {
      validAppointments.forEach(appointment => {
        try {
          const startDate = new Date(appointment.start);
          const date = format(startDate, 'EEEE, MMMM d');
          const time = format(startDate, 'h:mm a');
          
          if (!grouped[date]) {
            grouped[date] = {};
          }
          if (!grouped[date][time]) {
            grouped[date][time] = [];
          }
          grouped[date][time].push(appointment);
        } catch (error) {
          console.error("Error processing appointment:", appointment, error);
        }
      });
    } else {
      // For today/tomorrow, still group by time but include the date in the header
      const dateStr = format(
        dateFilter === "tomorrow" ? addDays(new Date(), 1) : new Date(),
        'EEEE, MMMM d'
      );
      grouped[dateStr] = {};
      
      validAppointments.forEach(appointment => {
        try {
          const startDate = new Date(appointment.start);
          const time = format(startDate, 'h:mm a');
          if (!grouped[dateStr][time]) {
            grouped[dateStr][time] = [];
          }
          grouped[dateStr][time].push(appointment);
        } catch (error) {
          console.error("Error processing appointment:", appointment, error);
        }
      });
    }
    return grouped;
  };

  const handleTodoClick = async (todo) => {
    console.log("Todo clicked:", todo);
    
    // The todo object itself contains the full appointment data
    if (todo) {
      onAppointmentClick(todo);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div 
            className="bg-indigo-50 rounded-sm shadow p-6 border border-indigo-100 cursor-pointer hover:bg-indigo-100 transition-colors duration-150"
            onClick={() => setActiveTab(0)}
          >
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-indigo-500">
                <CheckCircleIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-indigo-600">To Confirm</p>
                <p className="text-2xl font-semibold text-indigo-700">{getTodoCount("confirm")}</p>
              </div>
            </div>
          </div>
          <div 
            className="bg-green-50 rounded-sm shadow p-6 border border-green-100 cursor-pointer hover:bg-green-100 transition-colors duration-150"
            onClick={() => setActiveTab(1)}
          >
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-500">
                <DocumentTextIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-green-600">To Invoice</p>
                <p className="text-2xl font-semibold text-green-700">{getTodoCount("invoice")}</p>
              </div>
            </div>
          </div>
          <div 
            className="bg-amber-50 rounded-sm shadow p-6 border border-amber-100 cursor-pointer hover:bg-amber-100 transition-colors duration-150"
            onClick={() => setActiveTab(2)}
          >
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-amber-500">
                <ExclamationCircleIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-amber-600">To Pay</p>
                <p className="text-2xl font-semibold text-amber-700">{getTodoCount("payment")}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-sm shadow p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <ClockIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Today's Appointments</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {getTodaysAppointments().length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Todos Section */}
          <div className="bg-white rounded-sm shadow">
            <Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
              <Tab.List className="flex border-b border-gray-200">
                <Tab className={({ selected }) =>
                  `flex-1 py-4 px-4 text-center font-medium text-sm focus:outline-none
                  ${selected ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`
                }>
                  To Confirm ({getTodoCount("confirm")})
                </Tab>
                <Tab className={({ selected }) =>
                  `flex-1 py-4 px-4 text-center font-medium text-sm focus:outline-none
                  ${selected ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500 hover:text-gray-700'}`
                }>
                  To Invoice ({getTodoCount("invoice")})
                </Tab>
                <Tab className={({ selected }) =>
                  `flex-1 py-4 px-4 text-center font-medium text-sm focus:outline-none
                  ${selected ? 'text-amber-600 border-b-2 border-amber-600' : 'text-gray-500 hover:text-gray-700'}`
                }>
                  To Pay ({getTodoCount("payment")})
                </Tab>
                <div className="flex items-center px-4 border-l border-gray-200">
                  <button
                    onClick={() => setSortOrder(sortOrder === "newest" ? "oldest" : "newest")}
                    className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-sm"
                    title={sortOrder === "newest" ? "Sort by oldest first" : "Sort by newest first"}
                  >
                    {sortOrder === "newest" ? (
                      <ArrowDownIcon className="h-5 w-5" />
                    ) : (
                      <ArrowUpIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </Tab.List>
              <Tab.Panels className="p-4">
                <Tab.Panel>
                  <div className="space-y-4">
                    {getTodoItems("confirm").map((todo) => (
                      <FrontDeskTodoItem
                        key={todo._id}
                        todo={todo}
                        onClick={handleTodoClick}
                        onReschedule={() => {}}
                        onWideSidePanelClose={() => {}}
                      />
                    ))}
                  </div>
                </Tab.Panel>
                <Tab.Panel>
                  <div className="space-y-4">
                    {getTodoItems("invoice").map((todo) => (
                      <FrontDeskTodoItem
                        key={todo._id}
                        todo={todo}
                        onClick={handleTodoClick}
                        onReschedule={() => {}}
                        onWideSidePanelClose={() => {}}
                      />
                    ))}
                  </div>
                </Tab.Panel>
                <Tab.Panel>
                  <div className="space-y-4">
                    {getTodoItems("payment").map((todo) => (
                      <FrontDeskTodoItem
                        key={todo._id}
                        todo={todo}
                        onClick={handleTodoClick}
                        onReschedule={() => {}}
                        onWideSidePanelClose={() => {}}
                      />
                    ))}
                  </div>
                </Tab.Panel>
              </Tab.Panels>
            </Tab.Group>
          </div>

          {/* Appointments Section */}
          <AppointmentsSection
            appointments={getFilteredAppointments()}
            viewType={viewType}
            dateFilter={dateFilter}
            onAppointmentClick={handleAppointmentClick}
            onViewTypeChange={setViewType}
            onDateFilterChange={setDateFilter}
            getStatusBadgeClass={(status) => {
              switch (status) {
                case "Not Confirmed":
                  return "bg-yellow-100 text-yellow-800";
                case "Confirmed":
                  return "bg-green-100 text-green-800";
                case "Cancelled":
                  return "bg-red-100 text-red-800";
                case "Completed":
                  return "bg-blue-100 text-blue-800";
                default:
                  return "bg-gray-100 text-gray-800";
              }
            }}
          />
        </div>
      </div>

      {/* Appointment Summary Modal */}
      {selectedAppointment && (
        <AppointmentSummary
          open={isSummaryOpen}
          appointment={selectedAppointment}
          onClose={handleCloseSummary}
        />
      )}
    </div>
  );
} 